/**
 * Baseline config page (Phase 3 Action 41).
 *
 * Edits four blocks of tenant_config:
 *   - delivery.severity_taxonomy   (preset or custom)
 *   - delivery.default_channels    (Slack channel IDs, freeform v1)
 *   - delivery.default_schedule    (cron + timezone)
 *   - routing.default_owner_user_id (UUID)
 *
 * Loaded from GET /:id/baseline-config; saved via PUT (writes a new
 * tenant_config version).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';
import { TIMEZONE_OPTIONS, OTHER_TIMEZONE_SENTINEL } from '../lib/timezones';

interface ScheduleBlock {
  cron: string;
  timezone: string;
}

interface BaselineResponse {
  data: {
    delivery: {
      severity_taxonomy?: string[];
      default_channels?: string[];
      default_schedule?: ScheduleBlock;
    } | null;
    routing: {
      default_owner_user_id?: string | null;
    } | null;
  };
}

interface SaveBody {
  severity_taxonomy?: string[];
  default_channels?: string[];
  default_schedule?: ScheduleBlock;
  default_owner_user_id?: string | null;
}

const SEVERITY_PRESETS = [
  { label: 'Standard 3-tier — low / medium / high', value: ['low', 'medium', 'high'] },
  { label: 'Standard 4-tier — P0 / P1 / P2 / P3', value: ['p0', 'p1', 'p2', 'p3'] },
  { label: 'Critical-first — critical / high / medium / low / info', value: ['critical', 'high', 'medium', 'low', 'info'] },
] as const;

const CUSTOM_SENTINEL = '__custom__';

async function fetchJson<T>(url: string): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(apiPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
}

async function putBaseline(id: string, body: SaveBody): Promise<void> {
  const token = getStoredToken();
  const res = await fetch(apiPath(`/api/provisioning/tenants/${id}/baseline-config`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `save failed: ${res.status}`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b.message) message = b.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}

function matchPreset(taxonomy: string[]): number {
  for (let i = 0; i < SEVERITY_PRESETS.length; i++) {
    const p = SEVERITY_PRESETS[i].value;
    if (p.length === taxonomy.length && p.every((v, j) => v === taxonomy[j])) return i;
  }
  return -1;
}

export default function BaselineConfig() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const baselineQ = useQuery({
    queryKey: ['tenant-baseline', id],
    queryFn: () => fetchJson<BaselineResponse>(`/api/provisioning/tenants/${id}/baseline-config`),
    enabled: Boolean(id),
  });

  const [severityPreset, setSeverityPreset] = useState<string>('0');
  const [customSeverityText, setCustomSeverityText] = useState('');
  const [channelsText, setChannelsText] = useState('');
  const [cron, setCron] = useState('0 9 * * 1');
  const [tzSelect, setTzSelect] = useState('America/Los_Angeles');
  const [otherTz, setOtherTz] = useState('');
  const [defaultOwner, setDefaultOwner] = useState('');
  const [seeded, setSeeded] = useState(false);

  // Seed once from the loaded config.
  useEffect(() => {
    if (seeded || !baselineQ.data) return;
    const delivery = baselineQ.data.data.delivery;
    const routing = baselineQ.data.data.routing;
    if (delivery?.severity_taxonomy && delivery.severity_taxonomy.length > 0) {
      const idx = matchPreset(delivery.severity_taxonomy);
      if (idx >= 0) {
        setSeverityPreset(String(idx));
      } else {
        setSeverityPreset(CUSTOM_SENTINEL);
        setCustomSeverityText(delivery.severity_taxonomy.join(', '));
      }
    }
    if (delivery?.default_channels) setChannelsText(delivery.default_channels.join(', '));
    if (delivery?.default_schedule) {
      setCron(delivery.default_schedule.cron);
      const tz = delivery.default_schedule.timezone;
      const known = TIMEZONE_OPTIONS.find((o) => o.value === tz);
      if (known) {
        setTzSelect(tz);
      } else {
        setTzSelect(OTHER_TIMEZONE_SENTINEL);
        setOtherTz(tz);
      }
    }
    if (routing?.default_owner_user_id) setDefaultOwner(routing.default_owner_user_id);
    setSeeded(true);
  }, [baselineQ.data, seeded]);

  const effectiveSeverity = useMemo<string[]>(() => {
    if (severityPreset === CUSTOM_SENTINEL) {
      return customSeverityText
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return SEVERITY_PRESETS[Number(severityPreset)]?.value ? [...SEVERITY_PRESETS[Number(severityPreset)].value] : [];
  }, [severityPreset, customSeverityText]);

  const effectiveTimezone = tzSelect === OTHER_TIMEZONE_SENTINEL ? otherTz.trim() : tzSelect;

  const effectiveChannels = useMemo<string[]>(() => {
    return channelsText
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, [channelsText]);

  const mutation = useMutation({
    mutationFn: (body: SaveBody) => putBaseline(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-baseline', id] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      navigate(`/fleet/${id}`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: SaveBody = {
      severity_taxonomy: effectiveSeverity,
      default_channels: effectiveChannels,
      default_schedule: { cron, timezone: effectiveTimezone },
    };
    if (defaultOwner.trim() === '') {
      body.default_owner_user_id = null;
    } else if (/^[0-9a-f-]{36}$/i.test(defaultOwner.trim())) {
      body.default_owner_user_id = defaultOwner.trim();
    }
    mutation.mutate(body);
  }

  const submitting = mutation.isPending;
  const loading = baselineQ.isLoading;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link to={`/fleet/${id}`} className="text-sm text-slate-500 hover:text-slate-700">← Back to tenant</Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Baseline config</h1>
        <p className="mt-1 text-sm text-slate-600">Provisioning wizard · Step 4 of 5 — Baseline</p>
      </div>

      {mutation.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {mutation.error.message}
        </div>
      )}

      {loading ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 rounded-md border border-slate-200 bg-white p-6">
          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-slate-700">Severity taxonomy</label>
            <p className="mt-1 text-xs text-slate-500">Order matters — least to most severe. Used to rank reports in the weekly rollup.</p>
            <select
              value={severityPreset}
              onChange={(e) => setSeverityPreset(e.target.value)}
              className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {SEVERITY_PRESETS.map((p, i) => (
                <option key={i} value={String(i)}>{p.label}</option>
              ))}
              <option value={CUSTOM_SENTINEL}>Custom…</option>
            </select>
            {severityPreset === CUSTOM_SENTINEL && (
              <input
                type="text"
                value={customSeverityText}
                onChange={(e) => setCustomSeverityText(e.target.value)}
                placeholder="e.g. blocker, major, minor, trivial"
                className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            )}
            {effectiveSeverity.length > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                Will store: <span className="font-mono">[{effectiveSeverity.join(', ')}]</span>
              </p>
            )}
          </div>

          {/* Default channels */}
          <div>
            <label className="block text-sm font-medium text-slate-700">Default Slack channels</label>
            <p className="mt-1 text-xs text-slate-500">Comma-separated channel IDs (C…) where rollups land by default. Operators can override per report later.</p>
            <input
              type="text"
              value={channelsText}
              onChange={(e) => setChannelsText(e.target.value)}
              placeholder="C0123456789, C0987654321"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {effectiveChannels.length > 0 && (
              <p className="mt-2 text-xs text-slate-500">{effectiveChannels.length} channel{effectiveChannels.length === 1 ? '' : 's'} will be saved.</p>
            )}
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-slate-700">Default rollup schedule</label>
            <p className="mt-1 text-xs text-slate-500">Cron expression in the timezone below. Default = Mondays 09:00.</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <input
                type="text"
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                placeholder="0 9 * * 1"
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <select
                value={tzSelect}
                onChange={(e) => setTzSelect(e.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
                <option value={OTHER_TIMEZONE_SENTINEL}>Other (IANA name)…</option>
              </select>
            </div>
            {tzSelect === OTHER_TIMEZONE_SENTINEL && (
              <input
                type="text"
                value={otherTz}
                onChange={(e) => setOtherTz(e.target.value)}
                placeholder="Pacific/Auckland"
                className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            )}
          </div>

          {/* Routing — default owner */}
          <div>
            <label htmlFor="defaultOwner" className="block text-sm font-medium text-slate-700">Default owner user ID <span className="text-slate-400">(optional)</span></label>
            <p className="mt-1 text-xs text-slate-500">UUID of the fallback owner when no explicit routing matches. Leave blank to route to org admins.</p>
            <input
              id="defaultOwner"
              type="text"
              value={defaultOwner}
              onChange={(e) => setDefaultOwner(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${id}`)}
              disabled={submitting}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || effectiveSeverity.length === 0 || !effectiveTimezone}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save baseline'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
